import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { GenericAbortSignal } from 'axios';
import {
  resumeBlobFromResponse,
  resumeDownloadName,
} from 'src/helpers/resumeFile';
import ApiClient from 'src/helpers/apiClient';
import config from 'config';
import { RootState } from 'src/redux/index';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import { ProductType } from 'src/redux/types/employerLayout';
import type { BulkAnalysisFilter } from '../types/xray';

export const retryResumeAnalysis = createAsyncThunk(
  'gpt/resume-analysis/retry',
  async ({ analysis_id, job_id, org_id }: any, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getGptApi()
      ).gptApiRetryApplicantResumeAnalysis({
        analysisId: analysis_id,
        jobId: job_id,
        orgId: org_id,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const downloadCSV = createAsyncThunk(
  `gpt/download-csv`,
  async (
    {
      hiringStatus,
      searchQuery,
      jobId,
      orgId,
      filters,
    }: {
      hiringStatus?: string;
      searchQuery?: string;
      jobId: string;
      orgId: string;
      filters: BulkAnalysisFilter;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getGptApi()
      ).gptApiDownloadCsvApplicantResumeAnalysisPost(
        {
          jobId,
          orgId,
          listJobResumeAnalysisBody: {
            sort_by: filters.sortBy,
            sort_order: filters.sortOrder,
            hiring_status: hiringStatus,
            search_query: searchQuery,
            min_resume_analysis_experience_score:
              filters.minAnalysisExperienceScore,
            max_resume_analysis_experience_score:
              filters.maxAnalysisExperienceScore,
            min_resume_analysis_overall_score: filters.minAnalysisOverallScore,
            max_resume_analysis_overall_score: filters.maxAnalysisOverallScore,
            min_resume_analysis_skill_score: filters.minAnalysisSkillScore,
            max_resume_analysis_skill_score: filters.maxAnalysisSkillScore,
            min_rating: filters.minRating,
            max_rating: filters.maxRating,
            analysis_status: filters.analysisStatus,
            matching_skills: filters.matchingSkills,
            missing_skills: filters.missingSkills,
            matching_locations: filters.applicantLocation,
            start_date: filters.start_date,
            end_date: filters.end_date,
            applicant_tag: filters.applicantTags,
          },
        },
        {
          responseType: 'blob',
        },
      );
      const url = window.URL.createObjectURL(
        new Blob([(response as any).data]),
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'candidates.csv');
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAnalysedResumeList = createAsyncThunk(
  'gpt/getAnalysedList',
  async (
    {
      countOnly,
      hiringStatus,
      page,
      pageSize,
      searchQuery,
      jobId,
      orgId,
      filters,
    }: {
      countOnly?: boolean;
      hiringStatus?: string;
      page?: number;
      pageSize?: number;
      searchQuery?: string;
      jobId: string;
      orgId: string;
      filters: BulkAnalysisFilter;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getGptApi()
      ).gptApiListApplicantResumeAnalysisPost({
        jobId,
        orgId,
        listJobResumeAnalysisBody: {
          count_only: countOnly,
          page_size: pageSize,
          page,
          sort_by: filters.sortBy,
          sort_order: filters.sortOrder,
          hiring_status: hiringStatus,
          search_query: searchQuery,
          min_resume_analysis_experience_score:
            filters.minAnalysisExperienceScore,
          max_resume_analysis_experience_score:
            filters.maxAnalysisExperienceScore,
          min_resume_analysis_overall_score: filters.minAnalysisOverallScore,
          max_resume_analysis_overall_score: filters.maxAnalysisOverallScore,
          min_resume_analysis_skill_score: filters.minAnalysisSkillScore,
          max_resume_analysis_skill_score: filters.maxAnalysisSkillScore,
          min_rating: filters.minRating,
          max_rating: filters.maxRating,
          analysis_status: filters.analysisStatus,
          matching_skills: filters.matchingSkills,
          missing_skills: filters.missingSkills,
          matching_locations: filters.applicantLocation,
          start_date: filters.start_date,
          end_date: filters.end_date,
          applicant_tag: filters.applicantTags,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadAnalyzeResume = createAsyncThunk(
  'gpt/uploadNewResume',
  async ({ job_id, org_id, resume_file }: any, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getGptApi()
      ).gptApiBulkAnalysisUploadResume({
        jobId: job_id,
        orgId: org_id,
        resumeFile: resume_file,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCurrentAnalysedResume = createAsyncThunk(
  'gpt/getCurrentAnalysedResume',
  async (
    {
      analysis_id,
      job_id,
      org_id,
    }: { analysis_id: string; job_id: string; org_id: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await ApiClient.getGptApi()
      ).gptApiGetApplicantResumeAnalysis(
        { analysisId: analysis_id, jobId: job_id, orgId: org_id },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteResumeJobApplicant = createAsyncThunk(
  'gpt-job/deleteJobApplicant',
  async (
    {
      jobId,
      orgId,
      applicantsId,
    }: { applicantsId?: string[]; jobId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiDeleteResumeJobApplicant({
        jobId,
        orgId,
        deleteJobApplicantBody: { applicant_id_list: applicantsId },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateHiringStatus = createAsyncThunk(
  'gpt/getApiUpdateResumeHiringStatus',
  async (
    {
      jobId,
      applicantId,
      obj,
      orgId,
    }: {
      jobId: string;
      applicantId: string;
      obj: any;
      orgId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiUpdateHiringStatus({
        applicantId,
        jobId,
        orgId,
        updateHiringStatusBody: obj,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateRating = createAsyncThunk(
  'gpt/updateRating',
  async ({ jobId, orgId, applicantId, ratingValue }: any) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiUpdateApplicantRating({
        applicantId,
        jobId,
        orgId,
        ratingValue: String(ratingValue),
      });
      return response.data;
    } catch (err: any) {
      throw err.response.data;
    }
  },
);

let abortController: AbortController | null = null;
export const getGptJobs = createAsyncThunk(
  'org/{org_id}/job-posted-gpt',
  async ({ orgId }: any, { rejectWithValue }) => {
    try {
      if (abortController) {
        abortController.abort();
      }

      abortController = new AbortController();
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobPostedList({
        orgId,
        isArchieved: false,
        isGptJob: true,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchAnalysisSkills = createAsyncThunk(
  'job/analysis-skills',
  async (
    {
      jobId,
      orgId,
      query,
      page,
      pageSize,
    }: {
      jobId: string;
      orgId: string;
      query?: string;
      page?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetApplicantSkills({
        jobId,
        orgId,
        page,
        pageSize,
        searchQuery: query,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchLocationForFilter = createAsyncThunk(
  'job/location-for-filter',
  async (
    { jobId, orgId, query }: { jobId: string; orgId: string; query: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetApplicantLocation({ jobId, orgId, searchQuery: query });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getApplicantResume = async ({
  analysisId,
  jobId,
  orgId,
  signal,
}: {
  analysisId: string;
  jobId: string;
  orgId: string;
  signal: GenericAbortSignal;
}) => {
  const response = await (
    await ApiClient.getGptApi()
  ).gptApiApplicantResumeAnalysis(
    { analysisId, jobId, orgId },
    {
      signal,
      responseType: 'blob',
    },
  );
  return response;
};

export const downloadApplicantResume = createAsyncThunk(
  `jobs/download-Resume`,
  async ({ jobId, orgId, applicantId }: any, { getState }) => {
    const url = `${config.API_URL}/org/${orgId}/job/${jobId}/applicant/${applicantId}/resume-pdf`;
    const state = getState() as RootState;

    axios
      .post(
        url,
        {
          is_download: true,
          user_uploaded_resume: true,
        },
        {
          responseType: 'arraybuffer',
          headers: {
            Authorization: `Bearer ${state.auth?.token as string}`,
          },
        },
      )
      .then((response) => {
        const blob = resumeBlobFromResponse(response);
        const url1 = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url1;
        link.setAttribute('download', resumeDownloadName('resume', blob.type));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        throw error.response.data;
      });
  },
);

export const fetchAllBulkApplicantTags = createAsyncThunk(
  'get/all-bulk-applicant-tags',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetApplicantTags({ jobId, orgId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const fetchGptPostedJobList = createAsyncThunk(
  'org/{org_id}/jobs',
  async (
    {
      query,
      pageNo,
      pageSize,
      orgId,
      isGptJob,
      jobStatus,
      isCompetitive,
      productType,
      // isArchieved,
    }: {
      // isArchieved?: boolean;
      query?: string;
      pageNo: number;
      pageSize: number;
      orgId: string;
      isGptJob?: boolean;
      jobStatus?: string;
      isCompetitive?: boolean;
      productType?: ProductType;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobPostedList(
        {
          isCompetitive,
          isGptJob,
          jobStatus,
          orgId,
          pageNo,
          pageSize,
          productType,
          query,
        },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);
